'use client';

import { c as _c } from "react/compiler-runtime";
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { useQueryState } from 'nuqs';
import * as React from 'react';
import { cn } from '@/lib/utils';
type TabsProps = {
  urlKey?: string;
} & React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>;
const UrlControlledTabs = t0 => {
  const $ = _c(16);
  let className;
  let props;
  let ref;
  let t1;
  let urlKey;
  if ($[0] !== t0) {
    ({
      ref,
      className,
      urlKey,
      defaultValue: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
    $[4] = t1;
    $[5] = urlKey;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
    t1 = $[4];
    urlKey = $[5];
  }
  const defaultValue = t1 === undefined ? "" : t1;
  let t2;
  if ($[6] !== defaultValue) {
    t2 = {
      defaultValue,
      history: "replace"
    };
    $[6] = defaultValue;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const [urlValue, setUrlValue] = useQueryState(urlKey || "", t2);
  let t3;
  if ($[8] !== className) {
    t3 = cn(className);
    $[8] = className;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== props || $[11] !== ref || $[12] !== setUrlValue || $[13] !== t3 || $[14] !== urlValue) {
    t4 = <TabsPrimitive.Root ref={ref} value={urlValue} onValueChange={setUrlValue} {...props} className={t3} />;
    $[10] = props;
    $[11] = ref;
    $[12] = setUrlValue;
    $[13] = t3;
    $[14] = urlValue;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
};
const Tabs = t0 => {
  const $ = _c(23);
  let className;
  let controlledValue;
  let onValueChange;
  let props;
  let ref;
  let t1;
  let urlKey;
  if ($[0] !== t0) {
    ({
      ref,
      className,
      urlKey,
      value: controlledValue,
      onValueChange,
      defaultValue: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = controlledValue;
    $[3] = onValueChange;
    $[4] = props;
    $[5] = ref;
    $[6] = t1;
    $[7] = urlKey;
  } else {
    className = $[1];
    controlledValue = $[2];
    onValueChange = $[3];
    props = $[4];
    ref = $[5];
    t1 = $[6];
    urlKey = $[7];
  }
  const defaultValue = t1 === undefined ? "" : t1;
  if (urlKey) {
    let t2;
    if ($[8] !== className || $[9] !== defaultValue || $[10] !== props || $[11] !== ref || $[12] !== urlKey) {
      t2 = <UrlControlledTabs ref={ref} urlKey={urlKey} defaultValue={defaultValue} {...props} className={className} />;
      $[8] = className;
      $[9] = defaultValue;
      $[10] = props;
      $[11] = ref;
      $[12] = urlKey;
      $[13] = t2;
    } else {
      t2 = $[13];
    }
    return t2;
  }
  let t2;
  if ($[14] !== className) {
    t2 = cn(className);
    $[14] = className;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  let t3;
  if ($[16] !== controlledValue || $[17] !== defaultValue || $[18] !== onValueChange || $[19] !== props || $[20] !== ref || $[21] !== t2) {
    t3 = <TabsPrimitive.Root ref={ref} value={controlledValue} defaultValue={defaultValue} onValueChange={onValueChange} {...props} className={t2} />;
    $[16] = controlledValue;
    $[17] = defaultValue;
    $[18] = onValueChange;
    $[19] = props;
    $[20] = ref;
    $[21] = t2;
    $[22] = t3;
  } else {
    t3 = $[22];
  }
  return t3;
};
Tabs.displayName = TabsPrimitive.Root.displayName;
const TabsList = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      ref,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <TabsPrimitive.List ref={ref} className={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
TabsList.displayName = TabsPrimitive.List.displayName;

/**
 * TabsTrigger component for rendering tab navigation triggers
 * @param variant - Visual style variant for the tab trigger
 *   - 'default': Standard styling with muted background when active
 *   - 'white': White background styling when active
 */
const TabsTrigger = t0 => {
  const $ = _c(13);
  let className;
  let props;
  let ref;
  let t1;
  if ($[0] !== t0) {
    ({
      ref,
      className,
      variant: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
    $[4] = t1;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
    t1 = $[4];
  }
  const variant = t1 === undefined ? "default" : t1;
  const t2 = variant === "default" && "data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm data-[state=active]:font-medium";
  const t3 = variant === "white" && "data-[state=active]:bg-white data-[state=active]:text-black data-[state=active]:shadow-sm data-[state=active]:font-medium";
  let t4;
  if ($[5] !== className || $[6] !== t2 || $[7] !== t3) {
    t4 = cn("inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-pointer", t2, t3, className);
    $[5] = className;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== props || $[10] !== ref || $[11] !== t4) {
    t5 = <TabsPrimitive.Trigger ref={ref} className={t4} {...props} />;
    $[9] = props;
    $[10] = ref;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
};
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;
const TabsContent = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      ref,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("focus-visible:outline-hidden", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <TabsPrimitive.Content ref={ref} className={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
TabsContent.displayName = TabsPrimitive.Content.displayName;
export { UnderlineTabsTrigger } from './underline-tabs-trigger';
export { Tabs, TabsContent, TabsList, TabsTrigger };